/* Copyright (c) 2016-2023 Planticle Pty Ltd */
"use strict";

import _ from "underscore";
import * as clip from "../util/clip.js";
import googleMaps from "../util/google-maps.js";

// map - a google map
// feature provider - a function which takes tile (x,y,z)
// coordinates and returns a promise of features
function showLayer(map, featureProvider, options) {
  // non nested options
  options = _.defaults(options || {}, {});

  // ------

  var displayedTiles = {};

  var geometryGroups = {};

  function addPolygonToGroup(poly, id) {
    var group = geometryGroups[id];
    if(!group) {
      group = [];
      geometryGroups[id] = group;
    }
    group.push(poly);
    highlightPoly(poly, group.highlight);
  }

  function removePolygonFromGroup(poly, id) {
    var group = geometryGroups[id];
    if(!group) return;
    group = _.without(group, poly);
    if(group.length == 0) {
      delete geometryGroups[id];
    } else {
      geometryGroups[id] = group;
    }
  }

  function handlePolyMouseOver(evt) {
    highlightGroup(this.userData.id, true);
  }

  function handlePolyMouseOut(evt) {
    highlightGroup(this.userData.id, false);
  }

  function highlightGroup(id, highlight) {
    var group = geometryGroups[id];
    if(!group) return;
    group.highlight = highlight;
    _.each(group, function(poly) {
      highlightPoly(poly, highlight);
    });
  }

  function polygonStyle(feature, state) {
    var style = {
      fillColor: "black",
      fillOpacity: state.highlight ? 0.6 : 0.3
    };

    return options.polygonStyle ? options.polygonStyle(feature, state, style) : style;
  }

  function lineStyle(feature, state) {
    var style = {
      strokeColor: "black",
      strokeWeight: 1,
      strokeOpacity: 1
    };

    return options.lineStyle ? options.lineStyle(feature, state, style) : style;
  }

  function markerStyle(feature, state) {
    var style = {};
    return options.markerStyle ? options.markerStyle(feature, state, style) : style;
  }

  function highlightPoly(poly, highlight) {
    poly.setOptions(
      polygonStyle(poly.userData.attributes, { highlight: highlight, zoom: poly.tile.z })
    );
  }

  var visible = true;

  function updateOverlay() {
    var tiles = visible ? tilesInView() : [];

    var tilesToAdd = _.difference(_.keys(tiles), _.keys(displayedTiles));
    var tilesToRemove = _.difference(_.keys(displayedTiles), _.keys(tiles));

    _.each(tilesToAdd, function(key) {
      displayedTiles[key] = addTile(tiles[key]);
    });
    _.each(tilesToRemove, function(key) {
      removeTile(displayedTiles[key]);
      delete displayedTiles[key];
    });

    // console.log(options.layerID + ": " + _.size(geometryGroups) + " features");
    // console.log("z: " + z + ", sw: " + sw + ", ne: " + ne);
  }

  function setVisible(isVisible) {
    visible = isVisible;
    updateOverlay();
  }

  function refresh() {
    if(visible) {
      setVisible(false);
      setVisible(true);
    }
  }

  var featureFilter = _.constant(true);

  function setFeatureFilter(filter) {
    featureFilter = filter || _.constant(true);
    _.values(displayedTiles).forEach(updateTileFeatures);
  }

  function tileBounds(tile) {
    var zz = Math.pow(2, tile.z);
    var sw = new google.maps.Point(
      tile.x * 256 / zz,
      (tile.y + 1) * 256 / zz);
    var ne = new google.maps.Point(
      (tile.x + 1) * 256 / zz,
      tile.y * 256 / zz);
    var proj = map.getProjection();

    return new google.maps.LatLngBounds(proj.fromPointToLatLng(sw), proj.fromPointToLatLng(ne));
  }

  function addPolygonToTile(inputPoly, tile, id, attributes) {
    var paths = _.map(inputPoly.getArray(), function(ring) {
      return ring.getArray();
    });

    var polyOptions = _.extend(polygonStyle(attributes, { zoom: tile.z, highlight: false }), {
      paths: paths,
      clickable: true,
      strokeWeight: 0
    });
    var poly = new google.maps.Polygon(polyOptions);
    poly.userData = { id: id, layerID: options.layerID, attributes: attributes};
    poly.tile = tile;
    tile.polys[id].push(poly);

    addPolygonToGroup(poly, id);

    googleMaps.addSingleClickEvents(poly);

    google.maps.event.addListener(poly, 'mouseover', handlePolyMouseOver);
    google.maps.event.addListener(poly, 'mouseout', handlePolyMouseOut);

    _.each(options.polygonEvents || {}, function(handler, eventName) {
      google.maps.event.addListener(poly, eventName, handler);
    });
  }

  function addLineStringToTile(lineString, tile, id, attributes) {
    var lineOptions = _.extend(lineStyle(attributes, {zoom: tile.z}), {
      path: lineString.getArray(),
      clickable: false
    });
    var line = new google.maps.Polyline(lineOptions);
    line.userData = { id: id, layerID: options.layerID, attributes: attributes};
    // line.setMap(map);
    tile.lines[id].push(line);

    // Add outline
    if(options.outlineWidth) {
      var outline = new google.maps.Polyline({
        path: lineString.getArray(),
        clickable: false,
        strokeColor: options.outlineColor || "white",
        strokeWeight: options.outlineWidth,
        zIndex: -10
      })
      tile.lines[id].push(outline);
    }
  }

  function addMarkerToTile(coordinate, tile, id, key, attributes) {
    var marker = _.values(tile.markers).filter(m => m.getPosition().equals(coordinate))[0];
    if(!marker) {
      var markerOptions = _.extend(markerStyle(attributes, { zoom: tile.z }), {
        position: coordinate
      });
      var marker = new google.maps.Marker(markerOptions);

      _.each(options.markerEvents || {}, function(handler, eventName) {
        google.maps.event.addListener(marker, eventName, handler);
      });

      marker.userData = [];
    }
    tile.markers[id] = marker;
    marker.userData.push({ id: id, layerID: options.layerID, attributes: attributes });
  }

  function addFeatureToTile(feature, tile) {
    if(tile.released) {
      return;
    }

    var geometry = feature.data;

    switch(feature.type) {
      case "POLYGON":
        addPolygonsToTile(feature, [geometry], tile);
        break;
      case "MULTIPOLYGON":
        addPolygonsToTile(feature, geometry, tile);
        break;
      case "POINT":
        addPointToTile(feature, geometry[1], geometry[0], tile);
        break;
      default:
        console.log("Unsupported tile geometry: " + feature.type);
        return;
    }
  }

  function addPointToTile(feature, lat, lon, tile) {
    var id = feature.id;
    var markers;
    if(tile.markers[id]) {
        return; // Already added
    }

    var bounds = tileBounds(tile);
    var coordinate = new google.maps.LatLng(lat, lon);
    if(bounds.contains(coordinate)) {
      addMarkerToTile(coordinate, tile, id, _.omit(feature, 'data'));
    }
  }

  function addPolygonsToTile(feature, geometries, tile) {
    var id = feature.id;
    var bounds = tileBounds(tile);

    if(tile.polys[id]) {
      return; // Already added
    } else {
      tile.polys[id] = [];
      tile.lines[id] = [];
    }

    _.each(geometries, function(poly_geom) {
      var paths = _.map(poly_geom, function(path_geom) {
        return google.maps.geometry.encoding.decodePath(path_geom);
      });

      // Draw polygons
      var rawPoly = new google.maps.Data.Polygon(paths);
      var clippedPolys = clip.clipPolyToBounds(rawPoly, bounds);

      _.each(clippedPolys, function(clippedPoly) {
        addPolygonToTile(clippedPoly, tile, id, _.omit(feature, 'data'));
      });

      // Draw edges
      _.chain(paths).map(function(path) {
        var l = new google.maps.Data.LineString(path);
        return clip.clipLineStringToBounds(l, bounds);
      })
      .flatten()
      .each(function(lineString) {
        addLineStringToTile(lineString, tile, id, _.omit(feature, 'data'));
      });
    });
  }

  function removeFeatureFromTile(id, tile) {
    if(tile.polys[id]) {
      tile.polys[id].forEach(function(poly) {
        removePolygonFromGroup(poly, id);
        poly.setMap(null);
      });
      delete tile.polys[id];
    }

    if(tile.lines[id]) {
      tile.lines[id].forEach(function(line) {
        line.setMap(null);
      });
      delete tile.lines[id];
    }

    var marker = tile.markers[id];

    if(marker) {
      marker.userData = marker.userData.filter(function(f) {
        return f.id !== id;
      });
      if(marker.userData.length == 0) {
        marker.setMap(null);
      }
      delete tile.markers[id];
    }
  }

  function addTile(tile) {
    // console.log("add");
    // console.log(tile);

    tile.polys = {};
    tile.lines = {};
    tile.markers = {};

    featureProvider(tile.x, tile.y, tile.z).then(function(features) {
      if(!tile.released) {
        tile.features = features;
        updateTileFeatures(tile);
      }
    });

    return tile;
  }

  function updateTileFeatures(tile) {
    if(tile.features) {
      tile.features.forEach(function(feature) {
        var featureData = {
          userData: {
            attributes:  _.omit(feature, 'data')
          }
        };

        if(featureFilter(featureData)) {
          addFeatureToTile(feature, tile);

          _.flatten([tile.polys[feature.id], tile.lines[feature.id], tile.markers[feature.id]]).forEach(function(el) {
            if(el) { el.setMap(map); }
          });
        } else {
          removeFeatureFromTile(feature.id, tile);
        }
      });
    }
  }

  function removeTile(tile) {
    // Stop any pending requests from being processed
    tile.released = true;

    // Hide polys
    setTimeout(function() {
      if(tile.features) {
        tile.features.forEach(function(feature) {
          removeFeatureFromTile(feature.id, tile);
        });
      }
    }, 200);

    // console.log("remove");
    // console.log(tile);
  }

  function tilesInView() {
    var proj = map.getProjection();
    if(!proj) {
      return {};
    }

    var z = map.getZoom();
    var sw = proj.fromLatLngToPoint(map.getBounds().getSouthWest());
    var ne = proj.fromLatLngToPoint(map.getBounds().getNorthEast());
    var x, y;

    if(options.maxZoom && z > options.maxZoom) return {};
    if(options.minZoom && z < options.minZoom) return {};

    // Discrete zoom levels
    // z = Math.max(z - 2, 1);
    // z = _.find([13,9,5,1], function(a) { return z >= a })

    z = Math.min(z, 18);

    var zz = Math.pow(2,z);

    if(sw.x > ne.x) sw.x -= 256;

    var scale = zz / 256;

    sw.x *= scale; sw.y *= scale;
    ne.x *= scale; ne.y *= scale;

    var tiles = {};
    for(x = Math.floor(sw.x); x < Math.ceil(ne.x); x++) {
      for(y = Math.floor(ne.y); y < Math.ceil(sw.y); y++) { // y is flipped
        var tx = (x + zz) % zz;
        var key = tx + "," + y + "," + z;
        tiles[key] = {
          x: tx,
          y: y,
          z: z,
          key: key
        };
      }
    }

    return tiles;
  }

  google.maps.event.addListener(map, 'idle', updateOverlay);
  google.maps.event.addListener(map, 'projection_changed', updateOverlay);

  return {
    setFeatureFilter: setFeatureFilter,
    setVisible: setVisible,
    refresh: refresh
  };
}

export default {
	showLayer: showLayer
};
